import React from "react";
import { COMPONENT_TYPES } from "@/components/CategoryPage/constants";
import FAQ from "@/components/FAQ";
import Banner from "@/components/PageHome/components/Banner";
import { DeviceInfo } from "@/interface/deviceInfo";
import ArticleAyurveda from "@/components/PageHome/components/ArticleAyurveda";
import SectionHeader from "@/components/SectionHeader";
import ClustersByPillar from "@/components/CategoryPage/components/ClustersByPillar";
import SecondFold from "@/components/CategoryPage/components/SecondFold";
import TrimesterArticles from "@/components/CategoryPage/components/TrimesterArticles";
import TopTips from "@/components/PagePiller/components/TopTips";
import { useAppSelector } from "@/redux/hooks";
import DeviceSelector from "@/components/CategoryPage/redux/selecters/userAgent";
import Slider, { Slide } from "@/components/Slider";
import ShoppingComponent from "@/components/ShoppingComponent";
import IconRight from "$/icons/icon-right.svg";
import IconLeft from "$/icons/icon-left.svg";
import { COMPONENT_TYPES as COMMON_COMPONENT_TYPES } from "@/helpers/constants";
import DoctorsWidget from "@/components/DoctorsWidget";
import DiagnosticsWidget from "@/components/DiagnosticsWidget";
import VideosComponent from "@/components/VideosComponent";
import styles from "./CategoryPage.module.scss";

interface ComponentMappingProps {
  data: any;
  options?: any;
  isPharma?: boolean;
}

const CategoryComponentMapping = (props: ComponentMappingProps): React.ReactElement => {
  const { data, options = undefined, isPharma = false } = props;
  const type = data?.ComponentType;
  const deviceType: DeviceInfo = useAppSelector(DeviceSelector);

  switch (type) {
    case COMPONENT_TYPES.OBNudge:
      return <SecondFold {...{ deviceType, data, categorySlug: options?.categorySlug }} />;

    case COMPONENT_TYPES.ClustersByPillar:
      const Pillars = data?.Pillars?.data.map((pillar: any) => ({
        name: pillar?.attributes?.Name,
        slug: pillar?.attributes?.Slug,
      }));

      const ClustersArray = data?.Pillars?.data.reduce((acc, trimester) => {
        const trimesterSlug = trimester?.attributes?.Slug;
        const clusters = trimester?.attributes?.Clusters?.map((cluster) => ({
          ClusterType: cluster?.ClusterType,
          Name: cluster?.Name,
          Slug: cluster?.Slug,
        }));

        acc[trimesterSlug] = clusters;

        return acc;
      }, {});

      return (
        <>
          {ClustersArray &&Object.keys(ClustersArray)?.length > 0 ? (
            <ClustersByPillar {...{ Pillars, ClustersArray, deviceType, data }} />
          ) : null}
        </>
      );

    case COMPONENT_TYPES.ArticlesByPillar:
      return (
        <div className={`container spacingBetweenSections`}>
          <SectionHeader
            userAgent={deviceType}
            title={data?.Title}
            subTitle={data?.Description}
            className={styles.sectionHeader}
          />
          <>
            {data?.pillarArticles?.length > 0 && (
              <div className={`${styles.sliderWrapper} ${deviceType.isMobile ? styles.isMobile : ""}`}>
                <Slider
                  autoplay={false}
                  showDots={false}
                  dotsWrapperClass={styles.dotsWrapperClass}
                  dotClass={styles.dotClass}
                  slidesToShow={deviceType?.isDesktop ? 3 : 1.1}
                  dotClassActive={styles.dotClassActive}
                  columnGap={deviceType?.isDesktop ? 20 : 0}
                  showArrows={deviceType?.isDesktop}
                  nextArrow={<IconRight className={`${styles.arrows} ${styles.arrowNext}`} />}
                  prevArrow={<IconLeft className={`${styles.arrows} ${styles.arrowPrev}`} />}
                >
                  {data?.pillarArticles.map((trimesterArticlesObj, index) => (
                    <Slide index={index} key={index}>
                      <div className={styles.trimesterArticle}>
                        <TrimesterArticles data={trimesterArticlesObj} categorySlug={options?.categorySlug} isPharma={isPharma} key={index} assetPosition={index + 1} />
                      </div>
                    </Slide>
                  ))}
                </Slider>
              </div>
            )}
          </>
        </div>
      );
    case COMPONENT_TYPES.ArticlesByTag:
      return (
        <ArticleAyurveda
          articles={data?.ayurvedaArticles}
          Tag={data?.Tag}
          Title={data?.Title}
          userAgent={deviceType}
          category={options?.categorySlug}
          assetPosition={options?.index + 1}
          isPharma={isPharma}
        />
      );
    case COMPONENT_TYPES.AyurvedicCare:
      return (
        <div className="spacingBetweenSections">
          <Banner data={data} userAgent={deviceType} assetPosition={options?.index + 1} />
        </div>
      );
    case COMPONENT_TYPES.TopTips:
      return <TopTips data={data} userAgent={deviceType} />;
    case COMPONENT_TYPES.FAQ:
      return <FAQ data={data} />;
    case COMPONENT_TYPES.ShopByCatSlider:
      return <ShoppingComponent data={data} productInfo={options?.productInfo} userAgent={deviceType} />;
    case COMMON_COMPONENT_TYPES.DoctorConsult:
      return (
        <DoctorsWidget
          data={data}
          userAgent={deviceType}
          currentSpeciality={options?.currentSpeciality}
          doctors={options?.doctorsArray}
          utm_medium="category-page"
        />
      );
    case COMMON_COMPONENT_TYPES.LabTest:
      return (
        <div className="spacingBetweenSections">
          <DiagnosticsWidget
            data={data}
            userAgent={deviceType}
            diagnostics={options?.diagnostics}
            utm_medium="category-page"
          />
        </div>
      );
    case COMMON_COMPONENT_TYPES.DynamicVideos:
      return (
        <VideosComponent
          data={data}
          userAgent={deviceType}
          assetPosition={options?.index + 1}
          pageCategory={options?.categorySlug}
        />
      );

    default:
      return <> </>;
  }
};

export default CategoryComponentMapping;
